import {
  addItemToParent,
  adjustSectionHeights,
  duplicateItemAsSibling,
  findItemById,
  getDeepCopy,
  getValueByPath,
  reflowVerticalElements,
  removeItemById,
  replaceIdsInPositionedLayout,
  setValueByPath,
  updateBlockInLayout,
  updateOnlyElementConfigInLayout
} from "../utils";
import { useAuth0Context } from "../../../context/Auth0Context";
import { useParams } from "react-router-dom";
import { toastError, toastSuccess } from "../../../utils/toastUtils";

const useItemHandler = ({
  layoutConfig,
  setLayoutConfig,
  setSelectedItem,
  type,
  setSelectedItemPath,
  setMultiSelectedItems,
  gridSize
}) => {
  const { user } = useAuth0Context();
  const { appId } = useParams();
  const changedLayout = () => {
    setLayoutConfig((l) => {
      return adjustSectionHeights(l);
    });
  };
  const duplicateElement = (selectedItem, otherInfo) => {
    if (type !== "editable") return;
    // if (selectedItem.type === "section") return;

    if (selectedItem.type === "section") {
      const duplicatedSection = replaceIdsInPositionedLayout(getDeepCopy(selectedItem));
      const newLayoutConfig = [...layoutConfig, duplicatedSection];
      setLayoutConfig(newLayoutConfig);
      setSelectedItem(duplicatedSection);
      changedLayout();
      return;
    }

    if (otherInfo && otherInfo.path) {
      const mainElement = findItemById(layoutConfig || [], otherInfo.path[0]);
      const arrayElement = getValueByPath(mainElement, otherInfo.path[1]);
      let { layout: newLayout, items: duplicatedItem } = duplicateItemAsSibling(arrayElement, selectedItem, gridSize);
      const config = setValueByPath(mainElement, otherInfo.path[1], newLayout);
      // let final = updateOnlyElementConfigInLayout(layoutConfig, otherInfo.path[0], config.config);
      let final = updateBlockInLayout(layoutConfig, config);
      setLayoutConfig(final);
      setSelectedItem(duplicatedItem[0]);
    } else {
      let { layout: newLayout, items: duplicatedItem } = duplicateItemAsSibling(layoutConfig, selectedItem, gridSize);
      setLayoutConfig(newLayout);
      changedLayout();
      setSelectedItem(duplicatedItem[0]);
    }
  };

  const copyElement = async (selectedItem, copyType = "single_positioned_element") => {
    if (type === "view") return;

    // Prevent copying if any item is "section" or "column" in multi copy
    if (copyType === "multi_positioned_element") {
      const keys = Object.keys(selectedItem);
      const hasInvalidItem = keys.some((key) => {
        const item = selectedItem[key]?.item;
        return item?.type === "section" || item?.type === "column";
      });

      if (hasInvalidItem) {
        toastError("Cannot copy elements of type 'section' or 'column'");
        return;
      }
    }
    const copyStruct = {
      copyConfig: selectedItem,
      meta: {
        copiedAt: Date.now(),
        copiedBy: {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email
        },
        appId: appId
      },
      type: copyType
    };

    const deepCopy = getDeepCopy(copyStruct);

    try {
      await navigator.clipboard.writeText(JSON.stringify(deepCopy));
      toastSuccess(`${selectedItem.type || "All elements"} copied`);
    } catch (error) {
      toastError("Failed to copy");
    }
  };

  const pasteElement = async (selectedItem, otherInfo) => {
    if (type !== "editable") return;
    try {
      const text = await navigator.clipboard.readText(); // Read text from clipboard
      if (!text) return;

      const copiedElement = getDeepCopy(JSON.parse(text));
      let newCopiedItem;
      if (copiedElement.type === "multi_positioned_element") {
        let multi = [];
        Object.keys(copiedElement.copyConfig).forEach((key) => {
          const originalItem = copiedElement.copyConfig[key].item;
          const newItem = replaceIdsInPositionedLayout(originalItem);
          multi.push(newItem);
        });
        const shouldSkipReflow =
          selectedItem?.type === "block" &&
          multi.every(
            (item) => item.config?.type === "field" || item.config?.type === "label" || item.config?.type === "icon"
          ); // retain the original layout for fields, labels, and icons when multiselect when block is parent.
        newCopiedItem = shouldSkipReflow ? multi : reflowVerticalElements(multi);
      }
      if (copiedElement.type === "single_positioned_element") {
        const newItem = replaceIdsInPositionedLayout(copiedElement.copyConfig);
        const shouldSkipPositionReset =
          selectedItem?.type === "block" &&
          (newItem.config?.type === "field" || newItem.config?.type === "label" || newItem.config?.type === "icon");
        // retain the original layout for fields, labels, and icons when block is parent.
        newCopiedItem = shouldSkipPositionReset
          ? {
              ...newItem,
              style: {
                ...newItem.style,
                left: `${parseInt(newItem.style.left || "0") + 8}px`,
                top: `${parseInt(newItem.style.top || "0") + 8}px`
              }
            }
          : {
              ...newItem,
              style: { ...newItem.style, left: "0px", top: "0px" }
            };
      }
      if (!newCopiedItem) {
        toastError("Nothing to paste");
        return;
      }
      if (newCopiedItem.type === "section") {
        setLayoutConfig([...layoutConfig, newCopiedItem]);
        return;
      }
      if (newCopiedItem.type === "column") {
        if (selectedItem.type === "section") {
          const newLayout = addItemToParent(layoutConfig, newCopiedItem, selectedItem.id);
          setLayoutConfig(newLayout);
        } else {
          toastError("Column can only be added in section");
        }
        return;
      }
      if (!["section", "block", "column"].includes(selectedItem?.type)) {
        toastError("Pasting is not allowed here");
        return;
      }
      if (otherInfo && otherInfo.path) {
        const mainElement = findItemById(layoutConfig || [], otherInfo.path[0]);
        const arrayElement = getValueByPath(mainElement, otherInfo.path[1]);
        let newLayout = addItemToParent(arrayElement, newCopiedItem, selectedItem.id);
        const config = setValueByPath(mainElement, otherInfo.path[1], newLayout);
        let final = updateOnlyElementConfigInLayout(layoutConfig, otherInfo.path[0], config.config);
        setLayoutConfig(final);
      } else {
        const newLayout = addItemToParent(layoutConfig, newCopiedItem, selectedItem.id); // Add item as child
        setLayoutConfig(newLayout);
        changedLayout();
      }
      setSelectedItem(newCopiedItem);
      toastSuccess(`${newCopiedItem.type || "All elements"} pasted`);
    } catch (error) {
      toastError("Couldn't paste");
    }
  };

  const cutElement = (selectedItem, otherInfo) => {
    if (type !== "editable") return;
    if (selectedItem?.type !== "section") {
      copyElement(selectedItem);
      deleteElement(selectedItem, otherInfo);
    }
  };

  const deleteElement = (selectedItem, otherInfo) => {
    if (type !== "editable") return;
    if (!selectedItem) return;
    if (otherInfo && otherInfo.path) {
      const mainElement = findItemById(layoutConfig || [], otherInfo.path[0]);
      const arrayElement = getValueByPath(mainElement, otherInfo.path[1]);
      let newLayout = removeItemById(arrayElement, selectedItem.id);
      const config = setValueByPath(mainElement, otherInfo.path[1], newLayout);
      let final = updateOnlyElementConfigInLayout(layoutConfig, otherInfo.path[0], config.config);
      setLayoutConfig(final);
      setSelectedItem(null);
      setSelectedItemPath(null);
    } else {
      const newLayout = removeItemById(layoutConfig, selectedItem.id);
      setLayoutConfig(newLayout);
      changedLayout();
      setSelectedItem(null);
      setSelectedItemPath(null);
    }
  };

  const deleteMultiSelectedItems = (selectedMap) => {
    if (type !== "editable") return;
    if (!selectedMap || Object.keys(selectedMap).length === 0) return;

    let newLayoutConfig = [...layoutConfig];

    // Store edits by path group to avoid repeated updates
    const nestedGroups = {};

    Object.entries(selectedMap).forEach(([id, { _, otherInfo }]) => {
      if (otherInfo?.path) {
        const [parentId, nestedPath] = otherInfo.path;
        if (!nestedGroups[parentId]) {
          const parent = findItemById(newLayoutConfig, parentId);
          nestedGroups[parentId] = {
            parent,
            nestedPath,
            items: []
          };
        }
        nestedGroups[parentId].items.push(id);
      } else {
        // Top-level deletion
        newLayoutConfig = removeItemById(newLayoutConfig, id);
      }
    });
    // Apply nested deletions
    Object.values(nestedGroups).forEach(({ parent, nestedPath, items }) => {
      const nestedArray = getValueByPath(parent, nestedPath);
      const filtered = removeItemById(nestedArray, items);
      const updatedParent = setValueByPath(parent, nestedPath, filtered);
      newLayoutConfig = updateOnlyElementConfigInLayout(newLayoutConfig, parent.id, updatedParent.config);
    });

    setLayoutConfig(newLayoutConfig);
    setSelectedItem(null);
    setSelectedItemPath(null);
    setMultiSelectedItems(null);
    changedLayout();
  };

  return {
    duplicateElement,
    copyElement,
    pasteElement,
    cutElement,
    deleteElement,
    deleteMultiSelectedItems
  };
};

export default useItemHandler;
